import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supportAPI } from '../services/api';
import pusher from '../services/pusher';

const LiveSupport = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketMessage, setNewTicketMessage] = useState('');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (activeTicket) {
      fetchMessages(activeTicket.id);
      subscribeToTicket(activeTicket.id);
    }
  }, [activeTicket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTickets = async () => {
    try {
      const response = await supportAPI.getMyTickets();
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const fetchMessages = async (ticketId) => {
    try {
      const response = await supportAPI.getTicketMessages(ticketId);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const subscribeToTicket = (ticketId) => {
    const channel = pusher.subscribe(`ticket-${ticketId}`);
    
    channel.bind('new-message', (data) => {
      // Play sound for new message (only if from admin)
      if (data.message.sender_type === 'admin') {
        const audio = new Audio('/notif-newmessage.wav');
        audio.play().catch(err => console.log('Audio play failed:', err));
      }
      
      // Check for duplicates by ID
      setMessages((prev) => {
        const exists = prev.find(m => m.id === data.message.id);
        if (exists) return prev;
        return [...prev, data.message];
      });
    });

    channel.bind('ticket-closed', (data) => {
      // Update active ticket status
      setActiveTicket(prev => prev ? { ...prev, status: 'closed' } : null);
      // Refresh tickets list
      fetchTickets();
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await supportAPI.createTicket({
        subject: newTicketSubject,
        message: newTicketMessage,
      });
      setNewTicketSubject('');
      setNewTicketMessage('');
      setShowNewTicket(false);
      await fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      let errorMessage = 'Failed to create ticket';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message).join(', ');
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeTicket) return;

    setLoading(true);
    setError('');
    try {
      const response = await supportAPI.sendMessage(activeTicket.id, {
        message: newMessage,
      });
      setNewMessage('');
      setError('');
      // Don't add to state here - let Pusher handle it to avoid duplicates
    } catch (error) {
      console.error('Error sending message:', error);
      let errorMessage = 'Failed to send message';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message).join(', ');
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <nav className="navbar px-6 py-4 border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <img src="/logo-light.png" alt="Dexreaction" className="h-10" />
          </div>
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/dashboard')} className="btn-secondary">
              Dashboard
            </button>
            <button onClick={onLogout} className="btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-12 gap-6">
          {/* Ticket List */}
          <div className="col-span-4">
            <div className="reaction-card">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Support Tickets</h2>
                <button onClick={() => setShowNewTicket(true)} className="btn-primary px-4 py-2 text-sm">
                  New Ticket
                </button>
              </div>

              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => setActiveTicket(ticket)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      activeTicket?.id === ticket.id
                        ? 'border-purple-500 bg-purple-500/20'
                        : 'border-purple-500/20 hover:border-purple-500/50'
                    }`}
                  >
                    <div className="font-semibold mb-1">{ticket.subject}</div>
                    <div className="text-sm text-gray-400 mb-2 line-clamp-1">{ticket.message}</div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <span className={`w-3 h-3 rounded-full ${ticket.status === 'closed' ? 'bg-red-500' : 'bg-green-500'}`}></span>
                        <span className={`status-badge status-${ticket.status}`}>{ticket.status}</span>
                      </div>
                      <span className="text-xs text-gray-400">{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Chat Area */}
          <div className="col-span-8">
            {activeTicket ? (
              <div className="reaction-card flex flex-col" style={{ height: '70vh' }}>
                <div className="border-b border-purple-500/20 pb-4 mb-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <h2 className="text-xl font-bold">{activeTicket.subject}</h2>
                    <span className={`w-3 h-3 rounded-full ${activeTicket.status === 'closed' ? 'bg-red-500' : 'bg-green-500'}`}></span>
                    <span className={`status-badge status-${activeTicket.status} text-xs`}>{activeTicket.status}</span>
                  </div>
                  <p className="text-gray-400 text-sm">Ticket #{activeTicket.id.slice(0, 8)}</p>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs md:max-w-md px-4 py-3 rounded-lg ${
                          msg.sender_type === 'user'
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-700 text-gray-100'
                        }`}
                      >
                        <div className="text-sm mb-1">{msg.message}</div>
                        <div className="text-xs opacity-70">
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                {error && (
                  <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3 text-sm">
                    {error}
                  </div>
                )}
                {activeTicket.status === 'closed' ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                    <p className="text-red-400 font-semibold">This ticket has been closed</p>
                    <p className="text-gray-400 text-sm mt-1">If you need further assistance, please create a new ticket</p>
                  </div>
                ) : (
                  <form onSubmit={handleSendMessage} className="flex space-x-3">
                    <input
                      type="text"
                      placeholder="Type your message..."
                      className="input-field flex-1"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={loading}
                    />
                    <button type="submit" className="btn-primary px-6" disabled={loading}>
                      Send
                    </button>
                  </form>
                )}
              </div>
            ) : (
              <div className="reaction-card text-center py-12">
                <p className="text-gray-400 text-lg">Select a ticket to view conversation</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="modal" onClick={() => setShowNewTicket(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-6">Create New Ticket</h2>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Subject</label>
                <input
                  type="text"
                  className="input-field"
                  value={newTicketSubject}
                  onChange={(e) => setNewTicketSubject(e.target.value)}
                  required
                  placeholder="Brief description of your issue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  className="input-field"
                  rows="4"
                  value={newTicketMessage}
                  onChange={(e) => setNewTicketMessage(e.target.value)}
                  required
                  placeholder="Provide details about your issue"
                ></textarea>
              </div>
              {error && (
                <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm">
                  {error}
                </div>
              )}
              <div className="flex space-x-3">
                <button type="submit" className="btn-primary flex-1" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Ticket'}
                </button>
                <button type="button" onClick={() => {
                  setShowNewTicket(false);
                  setError('');
                }} className="btn-secondary flex-1">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveSupport;
